import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, AlertCircle, Phone, PhoneCall, Loader2, Eye, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL = '__all__';

const UnpaidGuardians = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const basePath = location.pathname.startsWith('/manager') ? '/manager/parents' : '/admin/parents';
    const [guardians, setGuardians] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [branchFilter, setBranchFilter] = useState(ALL);
    const [programFilter, setProgramFilter] = useState(ALL);
    const [callDialogOpen, setCallDialogOpen] = useState(false);
    const [selectedMobile, setSelectedMobile] = useState('');
    const [selectedParent, setSelectedParent] = useState(null);
    const [callNotes, setCallNotes] = useState('');
    const [savingCall, setSavingCall] = useState(false);
    const [sortField, setSortField] = useState('remaining');
    const [sortDir, setSortDir] = useState('desc');
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [smsDialogOpen, setSmsDialogOpen] = useState(false);
    const [smsSending, setSmsSending] = useState(false);

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir(field === 'remaining' || field === 'previous_debt' ? 'desc' : 'asc');
        }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <ArrowUpDown className="h-3 w-3 inline ml-1 opacity-40" />;
        return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 inline ml-1" /> : <ArrowDown className="h-3 w-3 inline ml-1" />;
    };

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [unpaidRes, programsRes] = await Promise.all([
                axios.get(`${API_URL}/api/unpaid-guardians`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
            ]);
            setGuardians(unpaidRes.data);
            setPrograms(programsRes.data || []);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    const openCallDialog = (g) => { setSelectedParent(g); setCallNotes(''); setSelectedMobile(g.mobile || ''); setCallDialogOpen(true); };

    const handleAddCall = async (e) => {
        e.preventDefault(); setSavingCall(true);
        try {
            await axios.post(`${API_URL}/api/calls`, { parent_id: selectedParent.id, mobile_called: selectedMobile || selectedParent.mobile || '', notes: callNotes }, { withCredentials: true });
            toast.success('Call logged'); setCallDialogOpen(false);
        } catch { toast.error('Failed to log call'); }
        finally { setSavingCall(false); }
    };

    // Branches list — distinct, sorted, drawn from the guardians' rows
    const branchOptions = useMemo(() => {
        const s = new Set();
        guardians.forEach(g => (g.branches || []).forEach(b => b && s.add(b)));
        return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [guardians]);

    // Programs list — only programs that appear on at least one unpaid guardian
    const programOptions = useMemo(() => {
        const usedIds = new Set();
        guardians.forEach(g => (g.program_ids || []).forEach(pid => pid && usedIds.add(pid)));
        return programs
            .filter(p => usedIds.has(p.id))
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || (a.name || '').localeCompare(b.name || ''));
    }, [programs, guardians]);

    const programNameById = useMemo(() => {
        const m = {};
        programs.forEach(p => { m[p.id] = p.name; });
        return m;
    }, [programs]);

    const filtered = useMemo(() => {
        const q = searchQuery.toLowerCase();
        const base = guardians.filter(g => {
            const matchesSearch = !q || (
                g.first_name?.toLowerCase().includes(q) ||
                g.last_name?.toLowerCase().includes(q) ||
                g.reg_code?.includes(searchQuery) ||
                g.mobile?.includes(searchQuery)
            );
            const matchesBranch = branchFilter === ALL || (g.branches || []).includes(branchFilter);
            const matchesProgram = programFilter === ALL || (g.program_ids || []).includes(programFilter);
            return matchesSearch && matchesBranch && matchesProgram;
        });
        const cmp = (a, b) => {
            let av, bv;
            if (sortField === 'code') { av = (a.reg_code || '').toString(); bv = (b.reg_code || '').toString(); }
            else if (sortField === 'name') { av = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase(); bv = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase(); }
            else if (sortField === 'previous_debt') { av = a.previous_debt || 0; bv = b.previous_debt || 0; }
            else { av = a.remaining || 0; bv = b.remaining || 0; }
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        };
        return [...base].sort(cmp);
    }, [guardians, searchQuery, branchFilter, programFilter, sortField, sortDir]);

    const totalDebt = filtered.reduce((sum, g) => sum + (g.remaining || 0), 0);
    const totalPrevDebt = filtered.reduce((sum, g) => sum + (g.previous_debt || 0), 0);

    const toggleSelect = (id) => setSelectedIds(prev => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });
    const allFilteredSelected = filtered.length > 0 && filtered.every(g => selectedIds.has(g.id));
    const toggleSelectAll = () => setSelectedIds(allFilteredSelected ? new Set() : new Set(filtered.map(g => g.id)));
    const selectedGuardians = guardians.filter(g => selectedIds.has(g.id));

    const handleSendSms = async () => {
        setSmsSending(true);
        try {
            const payload = selectedGuardians.map(g => ({ parent_id: g.id, amount: g.remaining || 0 }));
            const res = await axios.post(`${API_URL}/api/sms/payment-reminder`, { parents: payload }, { withCredentials: true });
            toast.success(`SMS sent: ${res.data.sent}${res.data.failed ? `, failed: ${res.data.failed}` : ''}`);
            setSmsDialogOpen(false);
            setSelectedIds(new Set());
        } catch { toast.error('Failed to send SMS reminders'); }
        finally { setSmsSending(false); }
    };

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="unpaid-guardians-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Unpaid Guardians</h1>
                        <p className="text-[#4B5563]">{filtered.length} guardians with outstanding balance | Total: {'\u20AC'}{totalDebt.toFixed(2)}{totalPrevDebt > 0 ? ` (incl. ${'\u20AC'}${totalPrevDebt.toFixed(2)} previous debt)` : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button className="bg-[#064E3B] hover:bg-[#047857]" disabled={selectedIds.size === 0} onClick={() => setSmsDialogOpen(true)} data-testid="sms-reminder-btn">
                            <MessageSquare className="h-4 w-4 mr-2" />SMS Reminder ({selectedIds.size})
                        </Button>
                        <Badge className="bg-[#EF4444] text-white text-lg px-4 py-1"><AlertCircle className="h-4 w-4 mr-2" />{guardians.length} unpaid</Badge>
                    </div>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="relative md:col-span-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                <Input placeholder="Search by name, code, or mobile..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="unpaid-search" />
                            </div>
                            <Select value={branchFilter} onValueChange={setBranchFilter}>
                                <SelectTrigger data-testid="branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL}>All branches</SelectItem>
                                    {branchOptions.map(b => (
                                        <SelectItem key={b} value={b}>{b}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={programFilter} onValueChange={setProgramFilter}>
                                <SelectTrigger data-testid="program-filter"><SelectValue placeholder="All programs" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL}>All programs</SelectItem>
                                    {programOptions.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {(branchFilter !== ALL || programFilter !== ALL) && (
                            <div className="flex items-center gap-2 mt-3">
                                <span className="text-xs text-[#4B5563]">Active filters:</span>
                                {branchFilter !== ALL && (
                                    <Badge variant="outline" className="text-xs">Branch: {branchFilter}</Badge>
                                )}
                                {programFilter !== ALL && (
                                    <Badge variant="outline" className="text-xs">Program: {programNameById[programFilter] || programFilter}</Badge>
                                )}
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => { setBranchFilter(ALL); setProgramFilter(ALL); }} data-testid="clear-filters">Clear</Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filtered.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead className="w-10"><input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} data-testid="sms-select-all" /></TableHead>
                                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('code')} data-testid="sort-by-code">Code<SortIcon field="code" /></TableHead>
                                        <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-by-name">Guardian<SortIcon field="name" /></TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Mobile</TableHead>
                                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('previous_debt')} data-testid="sort-by-prev-debt">Previous Debt<SortIcon field="previous_debt" /></TableHead>
                                        <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('remaining')} data-testid="sort-by-balance">Balance<SortIcon field="remaining" /></TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filtered.map(g => (
                                            <TableRow key={g.id} className="hover:bg-[#F9FAFB]" data-testid={`unpaid-row-${g.id}`}>
                                                <TableCell><input type="checkbox" checked={selectedIds.has(g.id)} onChange={() => toggleSelect(g.id)} data-testid={`sms-select-${g.id}`} /></TableCell>
                                                <TableCell><Badge variant="outline" className="font-mono tabular-nums">{g.reg_code}</Badge></TableCell>
                                                <TableCell><p className="font-medium">{g.first_name} {g.last_name}</p></TableCell>
                                                <TableCell className="text-sm text-[#4B5563]">{g.email}</TableCell>
                                                <TableCell><div className="flex items-center text-sm"><Phone className="h-3 w-3 mr-1 text-[#4B5563]" />{g.mobile}</div></TableCell>
                                                <TableCell className="text-right tabular-nums">
                                                    {g.previous_debt > 0
                                                        ? <span className="font-medium text-[#F59E0B]">{'\u20AC'}{g.previous_debt.toFixed(2)}</span>
                                                        : <span className="text-[#9CA3AF]">—</span>}
                                                </TableCell>
                                                <TableCell className="text-right font-bold text-[#EF4444] tabular-nums">{'\u20AC'}{g.remaining?.toFixed(2)}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button size="sm" variant="ghost" onClick={() => navigate(`${basePath}/${g.id}`)}><Eye className="h-4 w-4" /></Button>
                                                        <Button size="sm" variant="outline" onClick={() => openCallDialog(g)} data-testid={`call-${g.id}`}><PhoneCall className="h-3.5 w-3.5 mr-1" />Call</Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : <div className="flex flex-col items-center justify-center py-12"><AlertCircle className="h-12 w-12 text-[#10B981]/30 mb-4" /><p className="text-[#4B5563]">{(branchFilter !== ALL || programFilter !== ALL || searchQuery) ? 'No guardians match the current filters.' : 'All guardians have cleared their balance!'}</p></div>}
                    </CardContent>
                </Card>

                <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><PhoneCall className="h-5 w-5 text-[#064E3B]" />Log Call</DialogTitle></DialogHeader>
                        <form onSubmit={handleAddCall} className="space-y-4">
                            <div className="bg-[#FAF9F6] rounded-lg p-3 text-sm space-y-1">
                                <p><span className="text-[#4B5563]">Guardian:</span> <span className="font-medium">{selectedParent?.first_name} {selectedParent?.last_name}</span></p>
                                <p><span className="text-[#4B5563]">Code:</span> <span className="font-mono">{selectedParent?.reg_code}</span></p>
                                <p><span className="text-[#4B5563]">Called by:</span> <span className="font-medium">{user?.first_name} {user?.last_name}</span></p>
                                <p><span className="text-[#4B5563]">Time:</span> <span className="tabular-nums">{new Date().toLocaleString()}</span></p>
                            </div>
                            <div className="space-y-2">
                                <Label>Mobile Called</Label>
                                <div className="grid grid-cols-1 gap-2">
                                    {selectedParent?.mobile && (
                                        <label className={`flex items-center justify-between p-2 rounded-md border cursor-pointer ${selectedMobile === selectedParent.mobile ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-[#E5E7EB]'}`}>
                                            <span className="text-sm"><span className="text-[#4B5563] mr-2">Guardian 1</span><span className="font-mono">{selectedParent.mobile}</span></span>
                                            <input type="radio" name="mobile-called" value={selectedParent.mobile} checked={selectedMobile === selectedParent.mobile} onChange={() => setSelectedMobile(selectedParent.mobile)} data-testid="g1-mobile-radio" />
                                        </label>
                                    )}
                                    {selectedParent?.guardian2_mobile && (
                                        <label className={`flex items-center justify-between p-2 rounded-md border cursor-pointer ${selectedMobile === selectedParent.guardian2_mobile ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-[#E5E7EB]'}`}>
                                            <span className="text-sm"><span className="text-[#4B5563] mr-2">Guardian 2</span><span className="font-mono">{selectedParent.guardian2_mobile}</span></span>
                                            <input type="radio" name="mobile-called" value={selectedParent.guardian2_mobile} checked={selectedMobile === selectedParent.guardian2_mobile} onChange={() => setSelectedMobile(selectedParent.guardian2_mobile)} data-testid="g2-mobile-radio" />
                                        </label>
                                    )}
                                    {!selectedParent?.mobile && !selectedParent?.guardian2_mobile && (
                                        <Input placeholder="Enter number called" value={selectedMobile} onChange={e => setSelectedMobile(e.target.value)} />
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2"><Label>Call Summary</Label><textarea className="w-full min-h-[100px] rounded-md border border-[#D1D5DB] p-3 text-sm focus:ring-2 focus:ring-[#064E3B] focus:border-transparent" value={callNotes} onChange={e => setCallNotes(e.target.value)} placeholder="What happened during the call..." required /></div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setCallDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={savingCall} className="bg-[#064E3B] hover:bg-[#047857]">{savingCall && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save Call</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><MessageSquare className="h-5 w-5 text-[#064E3B]" />Send SMS Payment Reminder</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <p className="text-sm text-[#4B5563]">An SMS (sender: <span className="font-mono">NurulHuda</span>) will be sent to <strong>{selectedIds.size}</strong> selected guardian{selectedIds.size === 1 ? '' : 's'}:</p>
                            <div className="bg-[#FAF9F6] rounded-lg p-3 text-sm border border-[#E5E7EB]">
                                Nurulhuda Quranic School: Friendly reminder — €[balance] is outstanding on your account ([code]). Please log in to pay: [portal link]
                            </div>
                            <div className="max-h-32 overflow-y-auto text-xs text-[#4B5563] space-y-0.5">
                                {selectedGuardians.map(g => (
                                    <p key={g.id}>{g.first_name} {g.last_name} ({g.reg_code}) — {'\u20AC'}{(g.remaining || 0).toFixed(2)} — {g.mobile || 'no mobile'}</p>
                                ))}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleSendSms} disabled={smsSending} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="sms-reminder-confirm">
                                {smsSending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Send SMS
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default UnpaidGuardians;
